import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';

interface Conversation {
  id: string;
  pageId: string;
  senderId: string;
  phoneNumber: string;
  participantName?: string;
  lastMessage: string;
  lastMessageTime: string;
  status: string;
  statusId?: string;
  unreadCount?: number;
  crmIntegrated?: boolean;
  updatedAt?: number;
}

interface DraggableConversationCardProps {
  conversation: Conversation;
  onClick: () => void;
  onViewDetail: (e: React.MouseEvent) => void;
  onToggleCRM: (e: React.MouseEvent) => void;
  onViewHistory: (e: React.MouseEvent) => void;
  isLoadingCRM?: boolean;
}

const DraggableConversationCard: React.FC<DraggableConversationCardProps> = ({
  conversation,
  onClick,
  onViewDetail,
  onToggleCRM,
  onViewHistory,
  isLoadingCRM = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: conversation.id,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-brand-surface rounded-xl shadow-sm hover:shadow-md transition border border-brand-border hover:border-brand-yellow/40 overflow-hidden ${
        isDragging ? 'cursor-grabbing' : ''
      }`}
    >
      {/* ── Zona drag — arrastrar a otra columna ──────────────────────── */}
      <div
        {...listeners}
        {...attributes}
        title="Arrastra para mover de columna"
        className="group px-3 pt-3.5 pb-3 cursor-grab active:cursor-grabbing bg-brand-light/40 hover:bg-brand-yellow/5 transition-colors"
      >
        <div className="flex items-start gap-2">
          <div className="avatar-initial w-9 h-9 rounded-full bg-brand-dark flex items-center justify-center text-brand-yellow font-bold text-sm flex-shrink-0">
            {(conversation.participantName || conversation.phoneNumber).charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-brand-dark text-sm truncate">
              {conversation.participantName || 'Sin nombre'}
            </p>
            <p className="text-xs text-brand-mid truncate">{conversation.phoneNumber}</p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {(conversation.unreadCount ?? 0) > 0 && (
              <span className="bg-brand-yellow text-brand-dark text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {conversation.unreadCount}
              </span>
            )}
            {/* Ícono de drag — visible en hover para reforzar la zona */}
            <svg
              className="w-3.5 h-3.5 text-brand-border group-hover:text-brand-mid transition-colors flex-shrink-0"
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 8h16M4 16h16" />
            </svg>
          </div>
        </div>
      </div>

      {/* ── Zona click — cambiar estado ────────────────────────────────── */}
      <div
        onClick={onClick}
        title="Click para cambiar estado"
        className="px-3 py-2.5 cursor-pointer bg-brand-surface hover:bg-brand-light/60 transition-colors border-t border-brand-border/50"
      >
        <p className="text-xs text-brand-mid line-clamp-2 mb-1.5">
          {conversation.lastMessage}
        </p>
        <div className="flex items-center justify-between">
          <p className="text-[10px] text-brand-mid">{conversation.lastMessageTime}</p>
          {conversation.crmIntegrated && (
            <span className="text-[10px] bg-brand-yellow/20 text-brand-dark px-2 py-0.5 rounded-full font-semibold">
              CRM
            </span>
          )}
        </div>
      </div>

      {/* ── Botones de acción ──────────────────────────────────────────── */}
      <div className="flex gap-1.5 px-3 pb-3 pt-2 border-t border-brand-border">
        {/* Ver chat */}
        <button
          onClick={onViewDetail}
          className="flex-1 px-2 py-1.5 bg-brand-light text-brand-dark rounded-lg hover:bg-brand-mid/10 transition text-xs font-medium flex items-center justify-center gap-1"
          title="Ver conversación completa"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          Chat
        </button>

        {/* Historial */}
        <button
          onClick={onViewHistory}
          className="px-2 py-1.5 bg-brand-light text-brand-dark rounded-lg hover:bg-brand-mid/10 transition text-xs font-medium flex items-center justify-center"
          title="Ver historial de cambios"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>

        {/* CRM toggle */}
        <button
          onClick={onToggleCRM}
          className={`px-2 py-1.5 rounded-lg transition text-xs font-medium flex items-center justify-center ${
            conversation.crmIntegrated
              ? 'bg-brand-yellow/20 text-brand-dark hover:bg-brand-yellow/30'
              : 'bg-brand-light text-brand-mid hover:bg-brand-mid/10'
          }`}
          title={conversation.crmIntegrated ? 'Desmarcar CRM' : 'Marcar como CRM'}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {conversation.crmIntegrated ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M5 13l4 4L19 7" />
            )}
          </svg>
        </button>
      </div>
    </div>
  );
};

export default DraggableConversationCard;
